import moment from 'moment';
import { Formik } from 'formik';
import classNames from 'classnames';
import { useAppDispatch } from '../../../../effects/useAppDispatch';
import { useAppSelector } from '../../../../effects/useAppSelector';
import { FormError, Popup, PopupBody, PopupFooter } from '../../../controls';
import { FormFieldLabel } from '../../../forms';
import { constants, errorMessages } from '../../../../constants';
import { yup } from '../../../../validation/yup';
import { changeSubscriptionActions } from '../../../../actions/change-subscription.actions';
import { OnHoverTooltip } from "../../../common/OnHoverTooltip";
import { UserSlim } from '../../../../types/account/UserSlim';
import { EvalSubscriptionRequestType, SubscriptionType } from '../../../../types/billing/SubscriptionType';
import { DayPickerInput } from '../../../controls/DayPickerInput';

const trialDefaultDays = 2; // 3 with current day
const proDefaultYears = 1;

type subscriptionForm = {
    subscriptionType: string;
    subscriptionPeriodEnd?: Date;
    subscriptionPeriodEndRaw?: string;
};

interface Props {
    activeUsers: UserSlim[]
}

const minDate = moment()
    .startOf('day')
    .toDate();

const validationSchema = yup.object().shape({
    subscriptionType: yup.string().required(),
    subscriptionPeriodEndRaw: yup.date().when('subscriptionType', {
        is: (type: string) => type === 'Basic',
        then: () => yup.date().nullable(),
        otherwise: () => yup
            .date()
            .test('isValidDateFormat', errorMessages.invalidValue, (value) => moment(value, 'MM/DD/YYYY', true).isValid())
            .typeError(errorMessages.invalidValue)
            .min(minDate, errorMessages.dateShouldNotBeInPast)
            .required()
    })
});

export function ChangeEvalSubscriptionPopup({ activeUsers }: Props) {
    const dispatch = useAppDispatch();
    const { evalPopupVisible, company, isSubscriptionSavingFlag } = useAppSelector(s => s.changeSubscription);

    if (!evalPopupVisible || !company) {
        return null;
    }

    const isBasicSubscription = company.evalSubscription === SubscriptionType.basic;

    const getExpirationDate = () => { // Convert to local date and reset time
        const expDate = company.evalTrialPeriodEnd ?? company.evalSubscriptionExpiration;
        if (!expDate) {
            return undefined;
        }

        const momentDate = moment.utc(expDate);
        
        return new Date(momentDate.year(), momentDate.month(), momentDate.date());
    }

    const initialValues: subscriptionForm = {
        subscriptionType: company.evalTrialPeriodEnd ? SubscriptionType.trial : company.evalSubscription,
        subscriptionPeriodEnd: isBasicSubscription ? undefined :  getExpirationDate(),
        subscriptionPeriodEndRaw: isBasicSubscription ? '' : company?.subscriptionExpiration?.toString() ?? ''
    };

    const companyActiveMemberCount = activeUsers.filter((user: UserSlim) => user.companyId === company.id).length;

    const handleClose = () => {
        if (!isSubscriptionSavingFlag) {
            dispatch(changeSubscriptionActions.hide());
        }
    };

    const handleSubmit = (values: subscriptionForm) => {
        const { subscriptionType, subscriptionPeriodEnd } = values;

        const isTrial = subscriptionType === SubscriptionType.trial;
        let newSubscriptionType = isTrial || subscriptionType === SubscriptionType.pro
            ? EvalSubscriptionRequestType.EvalPro
            : EvalSubscriptionRequestType.EvalBasic;

        dispatch(
            changeSubscriptionActions.saveSubscription(
                company.id, newSubscriptionType, subscriptionPeriodEnd, isTrial)
        );
    };

    const getDefaultPeriodEnd = (subscription: string) => {
        switch (subscription) {
            case SubscriptionType.basic:
                return undefined;
            case SubscriptionType.trial:
                return moment().startOf('day').add(trialDefaultDays, 'd').toDate();
            default:
                return moment().startOf('day').add(proDefaultYears, 'y').toDate();
        }
    }

    return (
        <Popup
            title={
                <>
                    <span className="title">Change Subscription</span>
                    <OnHoverTooltip overlay={company.name}>
                        <span className="name-tag text-ellipsis">{company.name}</span>
                    </OnHoverTooltip>
                </>
            }
            modalClass="modal-change-subscription"
            onClose={handleClose}
        >
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnBlur
                onSubmit={handleSubmit}
                enableReinitialize={true}
            >
                {({ values, setValues, dirty, errors, isValid }) => {
                    return (
                        <>
                            <PopupBody>
                                <div className="flex-row">
                                    <div className="flex-item-equal">
                                        <dl className="definition-list style01">
                                            <dt>Company Role</dt>
                                            <dd>{company.rolesText}</dd>
                                        </dl>
                                    </div>
                                    <div className="flex-item-equal">
                                        <dl className="definition-list style01">
                                            <dt>Users</dt>
                                            <dd>{companyActiveMemberCount}</dd>
                                        </dl>
                                    </div>
                                </div>
                                <div className="flex-row">
                                    <div className="flex-item-equal">
                                        <FormFieldLabel required={true} text="Subscription Plan" />
                                        <select
                                            className="form-control form-select"
                                            name="subscriptionType"
                                            value={values.subscriptionType ?? ''}
                                            onChange={e => {
                                                const subscriptionPeriodEnd = getDefaultPeriodEnd(e.target.value)
                                                setValues(() => ({
                                                    subscriptionType: e.target.value,
                                                    subscriptionPeriodEnd,
                                                    subscriptionPeriodEndRaw: subscriptionPeriodEnd ? subscriptionPeriodEnd.toString() : ''
                                                }));
                                            }}
                                        >
                                            <option disabled value="">{constants.emptyPlaceholder}</option>
                                            <option value={SubscriptionType.basic}>Basic</option>
                                            <option value={SubscriptionType.pro}>Full Universe</option>
                                            <option value={SubscriptionType.trial}>Trial</option>
                                        </select>
                                        <FormError message={errors.subscriptionType} />
                                    </div>
                                    <div className="flex-item-equal">
                                        <FormFieldLabel required={true} text="Subscription Period End Date" />
                                        <div
                                            className={classNames('form-control-wrapper', {
                                                disabled: values.subscriptionType === 'Basic'
                                            })}
                                        >
                                            <DayPickerInput
                                                className={classNames('form-control', {
                                                    'is-invalid': errors.subscriptionPeriodEndRaw,
                                                })}
                                                emptyIfNull
                                                name="subscriptionPeriodEnd"
                                                selectedDate={values.subscriptionPeriodEnd}
                                                minimumDate={minDate}
                                                placeholder="MM/DD/YYYY"
                                                disabled={values.subscriptionType === SubscriptionType.basic}
                                                dateFormat={constants.dateShortFormat}
                                                onDayChange={(subscriptionPeriodEnd, subscriptionPeriodEndRaw) => {
                                                    setValues(prev => ({
                                                        ...prev,
                                                        subscriptionPeriodEnd: subscriptionPeriodEnd instanceof Date ? subscriptionPeriodEnd : undefined,
                                                        subscriptionPeriodEndRaw
                                                    }))
                                                }}
                                            />
                                            <FormError message={errors.subscriptionPeriodEndRaw} />
                                        </div>
                                    </div>
                                </div>
                            </PopupBody>
                            <PopupFooter>
                                <button
                                    className="btn btn-ghost"
                                    disabled={isSubscriptionSavingFlag}
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-main"
                                    type="submit"
                                    disabled={!dirty || isSubscriptionSavingFlag || !isValid}
                                    onClick={() => handleSubmit(values)}
                                >
                                    Save
                                </button>
                            </PopupFooter>
                        </>
                    );
                }}
            </Formik>
        </Popup>
    );
}
